
import CondicionesPrivacidadModal from "@/components/modals/CondicionesPrivacidad.vue";
import CursosModule from "@/store/modules/CursosModule";
import InscripcionModule from "@/store/modules/InscripcionModule";
import "moment/locale/es";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { ErrorMessage, useField, useForm } from "vee-validate";
import { computed, defineComponent, onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n/index";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { getModule } from "vuex-module-decorators";
import * as Yup from "yup";

interface BajaInscripcionCreate {
  dni: string;
  email: string;
  politicaPrivacidad: boolean;
}

interface ApiError {
  error: {
    statusCode: number;
    message: string;
  };
}

export default defineComponent({
  name: "kt-landing",
  components: {
    CondicionesPrivacidadModal,
    ErrorMessage,
  },

  setup() {
    const { t, te } = useI18n();
    const route = useRoute();
    const token = route.query.act;
    const router = useRouter();

    const store = useStore();
    const module = getModule(CursosModule, store);
    const moduleInscripciones = getModule(InscripcionModule, store);

    const loadingCursos = ref(false);
    const errorCargandoCursos = ref(false);
    const bajaInscripcion = ref(false);
    const perfilesAllowed = ref<string[]>([]);

    const profesionalAllowedValues = ["Violencias contra la mujer"];
    const particularAllowedValues = [
      "Digital",
      "Emprendimiento",
      "Orientación y empleo",
    ];
    const empresaAllowedValues = [
      "Igualdad en la empresa",
      "Digital",
      "Emprendimiento",
    ];

    onBeforeMount(() => {
      if (token) {
        loadingCursos.value = true;
        module.fetchById({ formularioId: token as string }).then(function () {
          if (module.errors) {
            errorCargandoCursos.value = true;
          }
          loadingCursos.value = false;
        });
      } else {
        errorCargandoCursos.value = false;
        loadingCursos.value = true;
        module.fetch().then(function () {
          if (module.errors) {
            errorCargandoCursos.value = true;
          }
          loadingCursos.value = false;
        });
      }
    });
    const datosCursos = computed(() => {
      const datos = module.cursos.map((c) => {
        const badges: string[] = [];
        if (profesionalAllowedValues.includes(c.areaTematica)) {
          badges.push("PROFESIONAL");
        }
        if (particularAllowedValues.includes(c.areaTematica)) {
          badges.push("PARTICULAR");
        }
        if (empresaAllowedValues.includes(c.areaTematica)) {
          badges.push("EMPRESA");
        }

        return { ...c, badges };
      });

      return datos;
    });
    const datosCurso = computed(() => {
      const badges: string[] = [];
      const curso = module.curso;

      if (profesionalAllowedValues.includes(curso?.areaTematica as string)) {
        badges.push("PROFESIONAL");
      }
      if (particularAllowedValues.includes(curso?.areaTematica as string)) {
        badges.push("PARTICULAR");
      }
      if (empresaAllowedValues.includes(curso?.areaTematica as string)) {
        badges.push("EMPRESA");
      }
      if (curso) {
        curso.badges = badges;
      }

      return curso;
    });

    watch(
      () => route.fullPath,
      (newFullPath) => {
        const formularioId = newFullPath.match(/act=([^&]*)/)?.[1];
        if (!formularioId) {
          module.setDetailData(null);
          module.fetch();
        }
      }
    );

    watch(datosCurso, () => {
      if (!datosCurso.value) return;

      perfilesAllowed.value = [];

      if (
        profesionalAllowedValues.includes(
          datosCurso.value.areaTematica as string
        )
      ) {
        perfilesAllowed.value.push("PROFESIONAL");
      }
      if (
        particularAllowedValues.includes(
          datosCurso.value.areaTematica as string
        )
      ) {
        perfilesAllowed.value.push("PARTICULAR");
      }
      if (
        empresaAllowedValues.includes(datosCurso.value.areaTematica as string)
      ) {
        perfilesAllowed.value.push("EMPRESA");
      }

      resetForm({
        values: {
          dni: "",
          email: "",
          politicaPrivacidad: false,
        },
      });
    });

    const isValidNIForNIE = (dni) => {
      const nifRegex = /^[0-9]{8}[A-Z]$/;
      const nieRegex = /^[XYZ][0-9]{7}[A-Z]$/;
      const validChars = "TRWAGMYFPDXBNJZSQVHLCKET";

      if (nifRegex.test(dni)) {
        const number = parseInt(dni.substring(0, 8), 10);
        const letter = validChars.charAt(number % 23);
        return letter === dni.charAt(8);
      }

      if (nieRegex.test(dni)) {
        // Cambiar la X, Y o Z a su equivalente numérico
        let number = dni.substring(1);
        switch (dni.charAt(0)) {
          case "X":
            number = "0" + number;
            break;
          case "Y":
            number = "1" + number;
            break;
          case "Z":
            number = "2" + number;
            break;
        }
        number = parseInt(number, 10);
        const letter = validChars.charAt(number % 23);
        return letter === dni.charAt(8);
      }
      return false;
    };

    const schema = Yup.object({
      dni: Yup.string()
        .required("DNI es obligatorio")
        .test("is-valid-dni", "Documento no válido", (value) =>
          isValidNIForNIE(value)
        ),
      email: Yup.string()
        .email("Debe ser un correo electrónico válido")
        .required("Email es obligatorio"),
      politicaPrivacidad: Yup.boolean()
        .required()
        .isTrue("Es obligatorio aceptar la política de privacidad"),
    });

    const { resetForm, handleSubmit, errors } = useForm<BajaInscripcionCreate>({
      validationSchema: schema,
    });

    const { value: dni } = useField("dni");
    const { value: email } = useField("email");
    const { value: politicaPrivacidad } = useField("politicaPrivacidad");

    const i18n = (text) => {
      if (te(text)) {
        return t(text);
      } else {
        return text;
      }
    };

    return {
      debug: 0,
      i18n,
      errors,
      token,
      errorCargandoCursos,
      loadingCursos,
      datosCurso,
      datosCursos,
      dni,
      email,
      politicaPrivacidad,
      bajaInscripcion,
      guardar: handleSubmit(async (values) => {
        const cursoId = datosCurso.value?.id as string;

        if (!cursoId) return;

        bajaInscripcion.value = true;

        try {
          await moduleInscripciones.baja({
            inscripcion: {
              dni: values.dni,
              email: values.email,
              estado: "CANCELADO",
              cursoId: cursoId,
            },
          });
          await Swal.fire({
            text: t("formularios.bajaInscripcionCursoDetalle.crear.exito"),
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: t("dialogs.okButton"),
            customClass: {
              confirmButton: "btn btn-primary",
            },
          });

          module.fetch();
          router.push("/home");
        } catch (err) {
          const apiError = err as ApiError;
          const mensajeError =
            apiError.error?.statusCode === 404
              ? apiError.error.message
              : t("formularios.bajaInscripcionCursoDetalle.crear.error");

          await Swal.fire({
            text: mensajeError,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: t("dialogs.okButton"),
            customClass: {
              confirmButton: "btn btn-primary",
            },
          });
        } finally {
          bajaInscripcion.value = false;
        }
      }),
      onItemTap: async (curso) => {
        module.setDetailData(curso);
        window.location.href =
          window.location.origin + "/#/home?act=" + curso.formularioId;
      },
      perfilesAllowed,
    };
  },
});
